import { FormEventHandler } from 'react'
import { useState } from 'react'
import { Title } from 'react-head'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import GetStartedBasicInfo from '../components/GetStartedBasicInfo'
import GetStartedEmail from '../components/GetStartedEmail'
import GetStartedOTP from '../components/GetStartedOTP'
import MinimalNav from '../components/MinimalNav'
import { fetcher } from '../utils/fetcher'
import {
  ChangeEvent,
  encodeSweUrlComponent,
  TemplateBM,
  UserBM,
} from '@bm-js/atlascloud-shared'
import { useAuthDispatch, useToast } from '../utils/hooks'
import { useApi } from '../utils/useApi'
import { AuthActionType } from '../reducers/AuthState.reducer'

const StyledGetStartedRoute = styled.div`
  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }
`

const GetStartedRoute = () => {
  const initialFormData = {
    email: '',
    fullName: '',
    otp: '',
    password: '',
    passwordRepeat: '',
    customerName: '',
    websiteName: '',
    websiteSubdomain: '',
    templateId: '',
    otpToken: '',
  }

  const toast = useToast()

  const initialErrors = { ...initialFormData }

  const navigate = useNavigate()
  const authDispatch = useAuthDispatch()

  const { data: templates = [] } = useApi<TemplateBM[]>('register/templates')

  const [step, setStep] = useState(0)
  const [formData, setFormData] = useState(initialFormData)
  const [errors, setErrors] = useState(initialErrors)
  const [loading, setLoading] = useState(false)

  const handleChange = (e: ChangeEvent) => {
    setErrors(initialErrors)
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmitEmail: FormEventHandler = async (e) => {
    e.preventDefault()
    setErrors(initialErrors)
    setLoading(true)
    const body = {
      fullName: formData.fullName,
      email: formData.email,
    }
    const { err, status } = await fetcher({
      path: 'register/verify-email',
      method: 'POST',
      body,
    })
    if (err) {
      setLoading(false)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (status === 400) return setErrors(err as any)
      return toast.error('Något gick fel')
    }
    setStep(step + 1)
    setLoading(false)
    setErrors(initialErrors)
  }

  const handleSubmitOTP: FormEventHandler = async (e) => {
    e.preventDefault()
    setErrors(initialErrors)
    setLoading(true)
    const body = {
      otp: formData.otp,
    }
    const { err, data, status } = await fetcher<{ token: string }>({
      path: 'register/verify-otp',
      method: 'POST',
      body,
    })
    if (err || !data) {
      setLoading(false)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (status === 400) return setErrors(err as any)
      else return toast.error('Något gick fel')
    }
    setFormData({
      ...formData,
      otpToken: data.token,
    })
    setStep(step + 1)
    setLoading(false)
    setErrors(initialErrors)
  }

  const handleSubmitBasicInfo: FormEventHandler = async (e) => {
    e.preventDefault()
    setErrors(initialErrors)
    setLoading(true)
    const templateId =
      templates.find((t) => t.name === 'onRegister')?._id || templates[0]?._id
    const body = {
      password: formData.password,
      customerName: formData.customerName,
      websiteName: formData.customerName,
      websiteSubdomain: encodeSweUrlComponent(formData.customerName),
      templateId,
    }
    const { err, status } = await fetcher({
      path: 'register/create-initial-data',
      method: 'POST',
      body,
      auth: formData.otpToken,
    })
    if (err) {
      setLoading(false)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (status === 400) return setErrors(err as any)
      else return toast.error('Något gick fel')
    }
    loginUser()
  }

  const loginUser = async () => {
    setLoading(true)
    setErrors(initialErrors)
    const { err, data } = await fetcher<{ user: UserBM; token: string }>({
      path: 'customer-no-auth/login',
      method: 'POST',
      body: {
        email: formData.email,
        password: formData.password,
      },
    })
    if (err || !data) {
      toast.info('Vänligen logga in')
      setLoading(false)
      navigate('/')
      return
    }

    authDispatch({
      type: AuthActionType.replaceState,
      data: { token: data.token },
    })
  }

  return (
    <StyledGetStartedRoute>
      <Title>Kom igång | Atlas Cloud</Title>
      <MinimalNav />
      <div className='flex h-screen w-full items-center justify-center'>
        <div className='w-96 max-w-screen-sm rounded-md bg-white p-4 shadow-lg'>
          {step === 0 && (
            <GetStartedEmail
              errors={errors}
              formData={formData}
              handleChange={handleChange}
              submit={handleSubmitEmail}
              loading={loading}
            />
          )}
          {step === 1 && (
            <GetStartedOTP
              errors={errors}
              formData={formData}
              handleChange={handleChange}
              submit={handleSubmitOTP}
              loading={loading}
            />
          )}
          {step === 2 && (
            <GetStartedBasicInfo
              errors={errors}
              formData={formData}
              handleChange={handleChange}
              submit={handleSubmitBasicInfo}
              loading={loading}
            />
          )}
        </div>
      </div>
    </StyledGetStartedRoute>
  )
}

export default GetStartedRoute
